// 草稿的自动保存：停手 800 毫秒存一次；同一时间只有一个保存在路上；
// 存不上时在本机留一份（只是这台设备上的保险，下次打开时如果比服务端的新就恢复）。
// 教练、改写、归档之前先 flush()，拿到最新的版本号再调用。

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../../api/client'
import { saveDraft, type ContextRef, type Draft } from '../../api/write'

export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict'

interface Content {
  title: string
  body: string
  contextRefs: ContextRef[]
}

const backupKey = (id: string) => `lingzhi.write.draft.${id}`
const same = (a: Content, b: Content) => a.title === b.title && a.body === b.body && JSON.stringify(a.contextRefs) === JSON.stringify(b.contextRefs)

function readBackup(d: Draft): (Content & { revision: number }) | null {
  try {
    const raw = localStorage.getItem(backupKey(d.id))
    if (!raw) return null
    const b = JSON.parse(raw) as Content & { revision: number }
    if (typeof b.body !== 'string' || typeof b.title !== 'string' || !(b.revision >= d.revision)) return null
    return { title: b.title, body: b.body, contextRefs: Array.isArray(b.contextRefs) ? b.contextRefs : d.contextRefs, revision: b.revision }
  } catch {
    return null
  }
}
function writeBackup(id: string, c: Content, revision: number) {
  try {
    localStorage.setItem(backupKey(id), JSON.stringify({ ...c, revision }))
  } catch {
    // 本机存不了也没关系
  }
}
function clearBackup(id: string) {
  try {
    localStorage.removeItem(backupKey(id))
  } catch {
    // 同上
  }
}

export function useDraft(draft: Draft, onSaved?: (d: Draft) => void) {
  const backup = useRef(readBackup(draft))
  const [content, setContent] = useState<Content>(() => backup.current ?? { title: draft.title, body: draft.body, contextRefs: draft.contextRefs })
  const [status, setStatus] = useState<SaveStatus>(backup.current && !same(backup.current, draft) ? 'dirty' : 'saved')
  const [savedAt, setSavedAt] = useState(draft.updatedAt)
  const [restored] = useState(() => !!backup.current && !same(backup.current, draft))
  const revision = useRef(draft.revision)
  const latest = useRef(content)
  const saved = useRef<Content>({ title: draft.title, body: draft.body, contextRefs: draft.contextRefs })
  const inflight = useRef<Promise<void> | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const conflict = useRef(false)
  const onSavedRef = useRef(onSaved)
  onSavedRef.current = onSaved
  latest.current = content

  const save = useCallback(async (): Promise<void> => {
    if (conflict.current) return
    if (inflight.current) await inflight.current
    const snap = latest.current
    if (same(snap, saved.current)) return
    setStatus('saving')
    const job = (async () => {
      try {
        const d = await saveDraft(draft.id, { revision: revision.current, ...snap })
        revision.current = d.revision
        saved.current = snap
        setSavedAt(d.updatedAt)
        clearBackup(draft.id)
        setStatus(same(latest.current, snap) ? 'saved' : 'dirty')
        onSavedRef.current?.(d)
      } catch (e) {
        writeBackup(draft.id, snap, revision.current)
        if (e instanceof ApiError && e.status === 409) {
          conflict.current = true
          setStatus('conflict')
        } else setStatus('error')
        throw e
      }
    })()
    inflight.current = job.catch(() => undefined).finally(() => (inflight.current = null))
    await job
  }, [draft.id])

  const update = useCallback((patch: Partial<Content>) => {
    setContent((c) => {
      const next = { ...c, ...patch }
      latest.current = next
      return next
    })
    if (conflict.current) return
    setStatus('dirty')
    clearTimeout(timer.current)
    timer.current = setTimeout(() => void save().catch(() => undefined), 800)
  }, [save])

  /** 先把没存的存上，返回最新的版本号。 */
  const flush = useCallback(async () => {
    clearTimeout(timer.current)
    if (inflight.current) await inflight.current
    if (!same(latest.current, saved.current)) await save()
    if (conflict.current) throw new Error('这份草稿在别处改过了，先载入最新的')
    return revision.current
  }, [save])

  // 本机恢复了比服务端新的内容：马上存一次
  useEffect(() => {
    if (restored) void save().catch(() => undefined)
    return () => clearTimeout(timer.current)
  }, [restored, save])

  // 离开页面前没存的，尽量存上（关掉标签页时本机已经留了一份）
  useEffect(() => {
    const onHide = () => {
      if (!same(latest.current, saved.current)) writeBackup(draft.id, latest.current, revision.current)
    }
    window.addEventListener('pagehide', onHide)
    return () => {
      window.removeEventListener('pagehide', onHide)
      onHide()
      if (!same(latest.current, saved.current) && !conflict.current) void save().catch(() => undefined)
    }
  }, [draft.id, save])

  return { ...content, update, status, savedAt, restored, flush, revision: () => revision.current }
}
